const listeners = new Set<(state: any) => void>();
import { telemetry } from '../spatial/utils/telemetry';
let currentIntent = 'idle';
let currentData: any = {};
let currentPanelMode = 'none'; // 'none' | 'settings' | 'chat' | 'map' | 'location' | 'booth' | 'pricing'
// let currentUserTier: 'free' | 'pro' | 'elite' = 'free'; // 🛡️ ATOM S1: Migrated to useWingmanStore

let currentSelectedLocation: any = null;
let currentHoveredLocationId: string | null = null;
let currentReasoningState: 'idle' | 'planning' | 'executing' | 'complete' = 'idle';

let currentConnectionStatus: 'online' | 'offline' | 'active' = 'offline';
let currentActiveMode: string = 'DEFAULT';
let currentRoutingState: 'IDLE' | 'ACTIVE' = 'IDLE';
let currentRoutingData: any = null;
let currentRoutingProfile: string = 'driving';
let currentSystemMode: 'spatial' | 'vault' | 'booth' | 'idle' = 'idle';
let currentItinerary: any[] = [];
let currentPortalMode: 'map' | 'media' | 'flights' | 'property' = 'map';
let currentPortalData: any = null;

let lastSnapshot: any = null;
let currentVisionObservation: any = null;

export interface QuotaContext {
    exhausted: true;
    code?: string;
    resource?: string;
    used?: number;
    limit?: number;
    tier?: 'free' | 'starter' | 'pro' | 'elite' | 'enterprise';
    message?: string;
}

export interface TripadvisorReviewItemContext {
    id?: string;
    title: string;
    rating: number;
    snippet: string;
    type?: string;
    responseSnippet?: string;
}

export interface TripadvisorContextData {
    placeId: string;
    placeName: string;
    totalReviews?: number;
    averageRating?: number;
    categoryRatings?: Record<string, number>;
    topReviews?: TripadvisorReviewItemContext[];
    updatedAt?: string;
}

const MAX_TRIPADVISOR_TEXT_LENGTH = 280;
const MAX_TRIPADVISOR_REVIEWS = 3;

function boundedText(value: unknown, maxLength = MAX_TRIPADVISOR_TEXT_LENGTH): string | undefined {
    if (typeof value !== 'string') return undefined;
    const text = value.trim();
    return text ? text.slice(0, maxLength) : undefined;
}

function rating(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 5
        ? value
        : undefined;
}

/** Provider content is retained as bounded evidence only; no arbitrary keys or instruction role survive. */
export function normalizeTripadvisorContext(value: unknown): TripadvisorContextData | null {
    const input = value as any;
    const placeId = boundedText(input?.placeId, 128);
    const placeName = boundedText(input?.placeName, 160);
    if (!placeId || !placeName) return null;

    const categoryRatings = Object.fromEntries(
        Object.entries(input?.categoryRatings || {})
            .slice(0, 10)
            .flatMap(([label, score]) => {
                const safeLabel = boundedText(label, 40);
                const safeScore = rating(score);
                return safeLabel && safeScore !== undefined ? [[safeLabel, safeScore]] : [];
            })
    );
    const topReviews = (Array.isArray(input?.topReviews) ? input.topReviews : [])
        .slice(0, MAX_TRIPADVISOR_REVIEWS)
        .flatMap((review: any) => {
            const title = boundedText(review?.title, 120);
            const snippet = boundedText(review?.snippet);
            const reviewRating = rating(review?.rating);
            if (!title || !snippet || reviewRating === undefined) return [];
            return [{
                ...(boundedText(review?.id, 128) ? { id: boundedText(review.id, 128) } : {}),
                title,
                rating: reviewRating,
                snippet,
                ...(boundedText(review?.type, 40) ? { type: boundedText(review.type, 40) } : {}),
                ...(boundedText(review?.responseSnippet) ? { responseSnippet: boundedText(review.responseSnippet) } : {}),
            }];
        });

    const averageRating = rating(input?.averageRating);
    const totalReviews = Number.isInteger(input?.totalReviews) && input.totalReviews >= 0
        ? input.totalReviews
        : undefined;
    return {
        placeId,
        placeName,
        ...(averageRating !== undefined ? { averageRating } : {}),
        ...(totalReviews !== undefined ? { totalReviews } : {}),
        ...(Object.keys(categoryRatings).length ? { categoryRatings } : {}),
        ...(topReviews.length ? { topReviews } : {}),
        ...(boundedText(input?.updatedAt, 40) ? { updatedAt: boundedText(input.updatedAt, 40) } : {}),
    };
}

let currentQuotaState: QuotaContext | null = null;
let currentTripadvisorContext: TripadvisorContextData | null = null;

const CANONICAL_TIERS = new Set(['free', 'starter', 'pro', 'elite', 'enterprise']);

function quotaNumber(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function normalizeQuotaContext(detail: any): QuotaContext {
    const tier = typeof detail?.tier === 'string' && CANONICAL_TIERS.has(detail.tier.toLowerCase())
        ? detail.tier.toLowerCase() as QuotaContext['tier']
        : undefined;
    const resource = [detail?.resource, detail?.feature, detail?.category, detail?.type, detail?.quotaType]
        .find(value => typeof value === 'string' && value.trim().length > 0);

    return {
        exhausted: true,
        ...(typeof detail?.code === 'string'
            ? { code: detail.code }
            : typeof detail?.error === 'string'
                ? { code: detail.error }
                : {}),
        ...(resource ? { resource: resource.trim() } : {}),
        ...(quotaNumber(detail?.used) !== undefined ? { used: quotaNumber(detail.used) } : {}),
        ...(quotaNumber(detail?.limit) !== undefined ? { limit: quotaNumber(detail.limit) } : {}),
        ...(tier ? { tier } : {}),
        ...(typeof detail?.message === 'string' && detail.message.trim() ? { message: detail.message.trim() } : {}),
    };
}

export function formatQuotaGuidance(quota: QuotaContext | null): string {
    if (!quota?.exhausted) return '';

    // If accounting is unavailable or loading, do not claim exhaustion
    if ((quota as any).unavailable || (quota as any).loading) {
        return "Usage accounting is temporarily updating. Your features remain available.";
    }

    // 0 used out of 250 means 250 remain
    if (quota.used !== undefined && quota.limit !== undefined && quota.limit > 0 && quota.used < quota.limit) {
        const remaining = quota.limit - quota.used;
        const tier = quota.tier ? ` on your ${quota.tier} plan` : '';
        return `You have ${remaining} of ${quota.limit} ${quota.resource || 'requests'} remaining today${tier}.`;
    }

    // Distinguish exhausted allowance from a feature unavailable on the plan
    if (quota.limit === 0) {
        const feat = quota.resource || 'This feature';
        const tier = quota.tier ? `on your ${quota.tier} plan` : 'on your current plan';
        return `${feat} is not included ${tier}. I can show you the available plans.`.trim();
    }

    const limitDescription = quota.resource ? `${quota.resource} limit` : 'current limit for this feature';
    const usage = quota.used !== undefined && quota.limit !== undefined
        ? ` (${quota.used} of ${quota.limit})`
        : '';
    const tier = quota.tier ? ` on your ${quota.tier} plan` : '';
    const resetTime = (quota as any).resetTime ? ` Resets at ${(quota as any).resetTime}.` : '';

    return `You've reached the ${limitDescription}${usage}${tier}.${resetTime} I can show you the available plans.`;
}

let currentStayInRegion = false;
let currentIntelligence: any = {
    weather: null,
    accommodations: [],
    restaurants: [],
    intelPriority: 'weather'
};

let currentSpatialContext = {
    lat: 0,
    lon: 0,
    zoom: 0,
    pitch: 0,
    bearing: 0
};

function shallowEqual(a: any, b: any) {
    if (a === b) return true;
    if (!a || !b) return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
        if (a[key] !== b[key]) return false;
    }
    return true;
}

let currentUserTier: 'free' | 'pro' | 'elite' = 'free';

let currentLanguage: string = 'English';
let currentTravelContext: 'local' | 'tourist' | 'business' | 'explorer' = 'explorer';
let currentMusicVibe: string = 'ambient';
let currentTone: 'professional' | 'casual' | 'enthusiastic' | 'mysterious' = 'casual';

export const contextState = {
    getQuotaState(): QuotaContext | null {
        return currentQuotaState;
    },
    setQuotaState(detail: any) {
        currentQuotaState = normalizeQuotaContext(detail);
        this.broadcast();
    },
    clearQuotaState() {
        if (!currentQuotaState) return;
        currentQuotaState = null;
        this.broadcast();
    },
    getSystemMode() {
        return currentSystemMode;
    },
    setSystemMode(mode: 'spatial' | 'vault' | 'booth' | 'idle') {
        if (currentSystemMode !== mode) {
            telemetry.info('CONTEXT_STATE', 'SYSTEM_MODE_CHANGE', { from: currentSystemMode, to: mode });
            currentSystemMode = mode;
            this.broadcast();
        }
    },
    getIntent() {
        return currentIntent;
    },
    getIntelligence() {
        return currentIntelligence;
    },
    setIntelligence(intel: any) {
        currentIntelligence = { ...currentIntelligence, ...intel };
        this.broadcast();
    },
    getStayInRegion() {
        return currentStayInRegion;
    },
    setStayInRegion(val: boolean) {
        if (currentStayInRegion === val) return;
        currentStayInRegion = val;
        telemetry.log({ subsystem: 'CONTEXT_STATE', event: 'STAY_IN_REGION', level: 'DEBUG', payload: { val } });
        this.broadcast();
    },
    getLanguage() { return currentLanguage; },
    setLanguage(lang: string) { currentLanguage = lang; this.broadcast(); },
    getTravelContext() { return currentTravelContext; },
    setTravelContext(ctx: 'local' | 'tourist' | 'business' | 'explorer') { currentTravelContext = ctx; this.broadcast(); },
    getMusicVibe() { return currentMusicVibe; },
    setMusicVibe(vibe: string) { currentMusicVibe = vibe; this.broadcast(); },
    getTone() { return currentTone; },
    setTone(tone: 'professional' | 'casual' | 'enthusiastic' | 'mysterious') { currentTone = tone; this.broadcast(); },
    getActiveMode() {
        return currentActiveMode;
    },
    setActiveMode(mode: string) {
        if (currentActiveMode === mode) return;
        currentActiveMode = mode;
        this.broadcast();
    },
    getData() {
        return currentData;
    },
    getPanelMode() {
        return currentPanelMode;
    },
    getRoutingState() {
        return currentRoutingState;
    },
    getRoutingData() {
        return currentRoutingData;
    },
    getRoutingProfile() {
        return currentRoutingProfile;
    },
    async fetchRoute(destinationLatLon: [number, number], profile: string = currentRoutingProfile) {
        if (currentRoutingProfile !== profile) {
            currentRoutingProfile = profile;
        }
        
        const startLonLat = currentSpatialContext.lon && currentSpatialContext.lat 
            ? [currentSpatialContext.lon, currentSpatialContext.lat]
            : [-80.1918, 25.7617]; 

        try {
            const { RouteGeometryAgent } = await import('../spatial/routing/routeGeometryAgent.js');
            const agent = new RouteGeometryAgent();
            const routeResponse = await agent.generateRoute(startLonLat, destinationLatLon, profile);
            if (routeResponse && routeResponse.primary) {
                currentRoutingData = routeResponse;
                this.broadcast();
            }
        } catch (error) {
            console.error('[contextState] Error fetching route:', error);
        }
    },
    getUserTier() {
        return currentUserTier;
    },
    setUserTier(tier: 'free' | 'pro' | 'elite') {
        if (currentUserTier === tier) return;
        currentUserTier = tier;
        
        // 🛡️ ATOM S1: Sync with authoritative store
        try {
            const { useWingmanStore } = require('../store/useWingmanStore');
            useWingmanStore.getState().setUserTier(tier);
        } catch (e) {
            // Store sync fails during early boot or unit tests
        }
        
        this.broadcast();
    },

    setRoutingState(state: 'IDLE' | 'ACTIVE') {
        if (currentRoutingState === state) return;
        currentRoutingState = state;
        this.broadcast();
    },
    getSpatialContext() {
        return currentSpatialContext;
    },
    setSpatialContext(context: { lat: number; lon: number; zoom: number; pitch?: number; bearing?: number }) {
        const nextContext = { ...currentSpatialContext, ...context };
        if (shallowEqual(currentSpatialContext, nextContext)) return;
        currentSpatialContext = nextContext;
        this.broadcast();
    },
    getSelectedLocation() {
        return currentSelectedLocation;
    },
    getHoveredLocationId() {
        return currentHoveredLocationId;
    },
    setHoveredLocationId(id: string | null) {
        if (currentHoveredLocationId === id) return;
        currentHoveredLocationId = id;
        this.broadcast();
    },
    getConnectionStatus() {
        return currentConnectionStatus;
    },
    setConnectionStatus(status: 'online' | 'offline' | 'active') {
        if (currentConnectionStatus === status) return;
        currentConnectionStatus = status;
        this.broadcast();
    },
    getItinerary() {
        return currentItinerary;
    },
    addBooking(booking: any) {
        currentItinerary = [...currentItinerary, { ...booking, id: Date.now() + '-' + Math.random().toString(36).substr(2, 9), status: 'pending', timestamp: new Date() }];
        this.broadcast();
    },

    updateBookingStatus(id: number, status: string) {
        const index = currentItinerary.findIndex(b => b.id === id);
        if (index !== -1 && currentItinerary[index].status === status) return;
        currentItinerary = currentItinerary.map(b => b.id === id ? { ...b, status } : b);
        this.broadcast();
    },
    getReasoningState() {
        return currentReasoningState;
    },
    setReasoningState(state: 'idle' | 'planning' | 'executing' | 'complete') {
        if (currentReasoningState === state) return;
        currentReasoningState = state;
        this.broadcast();
    },

    setState(intent: string, data: any = {}, panelMode?: string) {
        telemetry.log({ subsystem: 'CONTEXT_STATE', event: 'INTENT', level: 'DEBUG', payload: { intent } });
        const resolvedData = typeof data === 'function' ? data(currentData) : data;
        const intentChanged = intent !== currentIntent;
        const dataChanged = !shallowEqual(currentData, resolvedData);
        const panelChanged = panelMode && panelMode !== currentPanelMode;
 
        if (!intentChanged && !dataChanged && !panelChanged) return;
 
        telemetry.log({
            subsystem: 'CONTEXT_STATE',
            event: 'STATE_BEFORE_SET',
            level: 'DEBUG',
            payload: {
                intent: currentIntent,
                data: currentData,
                selectedLocation: currentSelectedLocation
            }
        });

        currentIntent = intent;
        currentData = resolvedData;
        if (panelMode) currentPanelMode = panelMode;
        
        /* Auto-switch to map mode disabled for lifecycle validation
        if (data?.mapMode && currentPanelMode === 'none') {
            currentPanelMode = 'map';
        }
        */

        // Only reset selected location if the intent has changed or explicitly requested 
        if (intentChanged) {
            currentSelectedLocation = null;
        }

        this.broadcast();
    },
    setIntent(intent: string) {
        this.setState(intent, currentData);
    },
    setData(data: any) {
        this.setState(currentIntent, data);
    },
    setPanelMode(mode: string) {
        if (currentPanelMode === mode) return;
        currentPanelMode = mode;
        this.broadcast();
    },
    setSelectedLocation(location: any) {
        if (currentSelectedLocation === location) return;
        if (location) {
            telemetry.log({ subsystem: 'CONTEXT_STATE', event: 'LOCATION_SELECTED', level: 'FORENSIC', payload: { name: location.name, lat: location.lat, lon: location.lon } });
        } else {
            telemetry.log({ subsystem: 'CONTEXT_STATE', event: 'LOCATION_CLEARED', level: 'FORENSIC' });
        }
        currentSelectedLocation = location;
        this.broadcast();
    },
    getPortalMode() {
        return currentPortalMode;
    },
    setPortalMode(mode: 'map' | 'media' | 'flights' | 'property') {
        if (currentPortalMode === mode) return;
        currentPortalMode = mode;
        this.broadcast();
    },
    getPortalData() {
        return currentPortalData;
    },
    setPortalData(data: any) {
        if (shallowEqual(currentPortalData, data)) return;
        currentPortalData = data;
        this.broadcast();
    },
    setVisionObservation(obs: any) {
        currentVisionObservation = obs;
        this.broadcast();
    },
    getVisionObservation(maxAgeMs: number = 60000) {
        if (!currentVisionObservation) return null;
        const age = Date.now() - (currentVisionObservation.timestamp || 0);
        if (age > maxAgeMs) {
            return null; // Expired observation
        }
        return currentVisionObservation;
    },
    clearVisionObservation() {
        currentVisionObservation = null;
        this.broadcast();
    },
    getTripadvisorContext(): TripadvisorContextData | null {
        return currentTripadvisorContext;
    },
    setTripadvisorContext(ctx: TripadvisorContextData | null) {
        const normalized = normalizeTripadvisorContext(ctx);
        if (shallowEqual(currentTripadvisorContext, normalized)) return;
        currentTripadvisorContext = normalized;
        this.broadcast();
    },
    clearTripadvisorContext() {
        if (!currentTripadvisorContext) return;
        currentTripadvisorContext = null;
        this.broadcast();
    },
    broadcast() {
        const nextSnapshot = { 
            intent: currentIntent, 
            data: currentData, 
            panelMode: currentPanelMode,
            activeMode: currentActiveMode,
            selectedLocation: currentSelectedLocation,
            hoveredLocationId: currentHoveredLocationId,
            visionObservation: currentVisionObservation,
            quotaState: currentQuotaState,
            connectionStatus: currentConnectionStatus,
            reasoningState: currentReasoningState,
            routingState: currentRoutingState,
            routingData: currentRoutingData,
            routingProfile: currentRoutingProfile,
            userTier: this.getUserTier(), // 🛡️ ATOM S1: Restored for broadcast compatibility
            portalMode: currentPortalMode,
            portalData: currentPortalData,
            stayInRegion: currentStayInRegion,
            intelligence: currentIntelligence,
            systemMode: currentSystemMode,
            spatialContext: currentSpatialContext,
            tripadvisorContext: currentTripadvisorContext
        };

        // 🛡️ ATOM 6 — INVARIANT GUARD
        // MISSION: Prevent silent contract corruption by validating snapshot shape.
        if (typeof nextSnapshot.intent !== 'string') {
            console.error('[CONTEXT_INVARIANT] Invalid intent type detected during broadcast');
        }

        // Deep check against last snapshot to catch any missing guard cases
        if (lastSnapshot && shallowEqual(lastSnapshot, nextSnapshot)) {
            // Check nested data object as well
            if (shallowEqual(lastSnapshot.data, nextSnapshot.data)) {
                telemetry.throttledLog('BROADCAST_SUPPRESSED', {
                    subsystem: 'CONTEXT_STATE',
                    event: 'BROADCAST_SUPPRESSED',
                    level: 'DEBUG',
                    payload: { reason: 'NO_CHANGE', intent: currentIntent }
                }, 5000);
                return;
            }
        }

        telemetry.log({
            subsystem: 'CONTEXT_STATE',
            event: 'BROADCAST',
            level: 'FORENSIC',
            payload: {
                intent: currentIntent,
                panelMode: currentPanelMode,
                systemMode: currentSystemMode,
                hasSelectedLocation: !!currentSelectedLocation,
                listenerCount: listeners.size
            }
        });

        lastSnapshot = nextSnapshot;
        listeners.forEach(fn => fn(nextSnapshot));
    },
    subscribe(listener: (state: any) => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }
};

if (typeof window !== 'undefined') {
    (window as any).contextState = contextState;
    window.addEventListener('WINGMAN_QUOTA_EXCEEDED', ((event: CustomEvent) => {
        contextState.setQuotaState(event.detail);
    }) as EventListener);
}
